import React, { useReducer, useEffect } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import DatePicker from '@material-ui/core/TextField';
import { json2csv } from 'json-2-csv';
import Svg from 'components/Svg';
import API from 'helpers/API';
import Spinner from 'components/Spinner';
import SelectWithValidation from 'components/SelectWithValidation';
import styles from './styles.scss';
import * as Reducers from './Reducers';
import StatsByFilterTable from './StatsByFilterTable';

const StatsByFilter = () => {
  const today = new Date();
  const { id: sinkConfigurationId } = useParams();
  const { location, search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const history = useHistory();

  const initialState = {
    startDate: format(addDays(today, -7), 'YYYY-MM-DD'),
    endDate: format(addDays(today, -1), 'YYYY-MM-DD'),
    dateRangeValidationMessage: null,
    selectedFilters: queryParams.getAll('shipment_filter_ids') || [],
    selectedCarriers: queryParams.getAll('carriers') || [],
    selectedPromisedDate: '',
    shipmentEventFilters: [],
    carriers: [],
    dataTables: null,
    dataForExport: [],
    tableDataReturnedCount: 0,
    loading: false,
  };

  const [state, dispatch] = useReducer(Reducers.statsByFilterReducer, initialState);

  const DATE_RANGE_VALIDATION_MESSAGE = (
    <>
      <p className={styles.dateRangeValidation}>
        The selected date range must be no more than a 30 day period and within the last 90 days.
      </p>
      <p className={styles.dateRangeValidation}>
        The from date must be earlier than, or the same day as, the to date.
      </p>
    </>
  );

  const addDataForExport = (data) => {
    dispatch({ type: Reducers.SET_DATA_FOR_EXPORT, dataForExport: data });
  };

  const downloadCsv = (data) => {
    const blob = new Blob([data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stats.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const startDateIsValid = (startDateToValidate) => {
    const earliestFromDate = new Date(addDays(today, -90));
    const dateToValidate = new Date(startDateToValidate);

    if (dateToValidate >= earliestFromDate
            && dateToValidate <= new Date(state.endDate)) {
      return true;
    }

    dispatch({ type: Reducers.SET_DATE_RANGE_VALIDATION_MESSAGE, dateRangeValidationMessage: DATE_RANGE_VALIDATION_MESSAGE });
    return false;
  };

  const endDateIsValid = (endDateToValidate) => {
    const latestToDate = new Date(addDays(state.startDate, 30));
    const dateToValidate = new Date(endDateToValidate);

    if (dateToValidate <= latestToDate
            && dateToValidate <= today
            && dateToValidate >= new Date(state.startDate)) {
      return true;
    }

    dispatch({ type: Reducers.SET_DATE_RANGE_VALIDATION_MESSAGE, dateRangeValidationMessage: DATE_RANGE_VALIDATION_MESSAGE });
    return false;
  };

  const handleFilterSelected = (event) => {
    if (state.selectedFilters.includes(event.value)) {
      return;
    }
    dispatch({ type: Reducers.ADD_SELECTED_FILTER, newlySelectedFilter: event.value });
  };

  const handleCarrierSelected = (event) => {
    if (state.selectedCarriers.includes(event.value)) {
      return;
    }
    dispatch({ type: Reducers.ADD_SELECTED_CARRIER, newlySelectedCarrier: event.value });
  };

  const getUrlFriendlyDate = (date) => {
    const localeOptions = { minimumIntegerDigits: 2, useGrouping: false };
    return `${date.getFullYear()}${(date.getMonth() + 1).toLocaleString('en-GB', localeOptions)}${date.getDate().toLocaleString('en-GB', localeOptions)}`;
  };

  const buildDatesArray = () => {
    const dates = [];
    let loopDate = new Date(state.startDate);
    while (loopDate <= new Date(state.endDate)) {
      dates.push(getUrlFriendlyDate(loopDate));
      loopDate = addDays(loopDate, 1);
    }
    return dates;
  };

  const handleLoadClick = () => {
    if (startDateIsValid(state.startDate) && endDateIsValid(state.endDate)) {
      dispatch({ type: Reducers.CLEAR_EXPORT_DATA });

      const dates = buildDatesArray();
      dispatch({
        type: Reducers.SET_DATA_TABLES,
        dataTables: state.selectedFilters.map(filter => (
          <StatsByFilterTable
            key={filter}
            sinkConfigurationId={sinkConfigurationId}
            filter={filter}
            dates={dates}
            carrierReferences={state.selectedCarriers}
            promisedDates={state.selectedPromisedDate ? [getUrlFriendlyDate(new Date(state.selectedPromisedDate))] : []}
            filterName={state.shipmentEventFilters.find(f => f.id === filter).name}
            carrierData={state.carriers}
            addDataForExport={addDataForExport}
          />
        )),
      });
      return;
    }

    dispatch({ type: Reducers.SET_DATE_RANGE_VALIDATION_MESSAGE, dateRangeValidationMessage: DATE_RANGE_VALIDATION_MESSAGE });
  };

  useEffect(() => {
    async function fetchData() {
      dispatch({ type: Reducers.SET_LOADING });
      const shipmentEventFiltersResponse = await API.getShipmentFilters();
      const carriersResponse = await API.getAvailableCarriers();
      dispatch({
        type: Reducers.DATA_LOADED,
        shipmentEventFilters: shipmentEventFiltersResponse.shipment_filters || [],
        carriers: carriersResponse.carriers || [],
      });
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (
      state.selectedFilters.length > 0
      && state.startDate
      && state.endDate
      && state.shipmentEventFilters.length > 0
      && state.carriers.length > 0
    ) {
      handleLoadClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shipmentEventFilters, state.carriers]);

  // Sync URL with selectedFilters and selectedCarriers
  useEffect(() => {
    const params = new URLSearchParams([
      ...state.selectedCarriers.map(carrier => ['carriers', carrier]),
      ...state.selectedFilters.map(filter => ['shipment_filter_ids', filter]),
    ]);
    history.replace({ ...location, search: params.toString() });
  }, [state.selectedFilters, state.selectedCarriers, history, location]);

  const selectedFiltersList = () => (
    state.shipmentEventFilters && state.shipmentEventFilters.length > 0 && state.selectedFilters.map(filter => (
      <li key={filter}>
        <span
          role="button"
          onClick={() => dispatch({ type: Reducers.REMOVE_SELECTED_FILTER, filterToRemove: filter })}
        >
          <Svg name="crossCircle" />
        </span>
        <p>
          {state.shipmentEventFilters.find(f => f.id === filter).name}
        </p>
      </li>
    ))
  );

  const selectedCarriersList = () => (
    state.carriers && state.carriers.length && state.selectedCarriers.map(carrier => (
      <li key={carrier}>
        <span
          role="button"
          onClick={() => dispatch({ type: Reducers.REMOVE_SELECTED_CARRIER, carrierToRemove: carrier })}
        >
          <Svg name="crossCircle" />
        </span>
        <p>
          {state.carriers.find(c => c.key === carrier).name}
        </p>
      </li>
    ))
  );

  if (state.loading) {
    return (
      <Spinner />
    );
  }

  return (
    <div className={styles.statsForm}>
      <div className={styles.statsFormSection}>
        <h5>Required fields</h5>
        <div className={styles.periodContainer}>
          <label className={styles.periodsLabel}>Date range from:</label>
          <div className={styles.periods}>
            <DatePicker
              value={state.startDate}
              type="date"
              onChange={e => {
                if (startDateIsValid(e.target.value)) {
                  dispatch({ type: Reducers.SET_START_DATE, startDate: e.target.value });
                }
              }}
              id="start-date"
            />

          </div>
          <label className={styles.periodsLabel}>to:</label>
          <div className={styles.periods}>
            <DatePicker
              value={state.endDate}
              type="date"
              onChange={e => {
                if (endDateIsValid(e.target.value)) {
                  dispatch({ type: Reducers.SET_END_DATE, endDate: e.target.value });
                }
              }}
              id="end-date"
            />
          </div>
        </div>
        {state.dateRangeValidationMessage !== null && (state.dateRangeValidationMessage)}
        <div className={styles.periodContainer}>
          <label className={styles.periodsLabel}>Add Shipment Event Filter:</label>
          <div className={styles.filterSelect}>
            <SelectWithValidation
              name="filter-select"
              onChange={e => handleFilterSelected(e)}
              options={state.shipmentEventFilters.map(x => ({ value: x.id, label: x.name }))}
            />
          </div>
          <div className={styles.selection}>
            Selected Shipment Event Filters:
            {state.selectedFilters && <ul>{selectedFiltersList()}</ul>}
          </div>
        </div>
        <div className={styles.periodContainer}>
          <button
            type="button"
            className={styles.button}
            onClick={handleLoadClick}
            disabled={state.loading || state.selectedFilters.length === 0 || !state.startDate || !state.endDate}
          >
            Load Data
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={() => {
              downloadCsv(json2csv(state.dataForExport));
            }}
            disabled={state.tableDataReturnedCount === 0 || state.tableDataReturnedCount < state.selectedFilters.length}
          >
            Export Data
          </button>
        </div>
      </div>

      <div className={styles.statsFormSection}>
        <h5>Optional fields</h5>
        <div className={styles.periodContainer}>
          <label className={styles.periodsLabel}>Promised Date:</label>
          <div className={styles.periods}>
            <DatePicker
              value={state.selectedPromisedDate}
              type="date"
              onChange={e => dispatch({ type: Reducers.SET_SELECTED_PROMISED_DATE, promisedDate: e.target.value })}
              id="promised-date"
            />
          </div>
        </div>
        <div className={styles.periodContainer}>
          <label className={styles.periodsLabel}>Add Carrier:</label>
          <div className={styles.filterSelect}>
            <SelectWithValidation
              name="carrier-select"
              onChange={e => handleCarrierSelected(e)}
              options={state.carriers.map(x => ({ value: x.key, label: x.name }))}
            />
          </div>
          <div className={styles.selection}>
            Selected Carriers:
            {state.selectedCarriers && <ul>{selectedCarriersList()}</ul>}
          </div>
        </div>
      </div>

      <div className={styles.statsDataSection}>
        {state.dataTables !== null && (
          state.dataTables
        )}
      </div>
    </div>
  );
};
export default StatsByFilter;
